import { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { APP_VERSION, BUILD_TIME, formatVersion, getPageVersionForPath } from '../versionInfo';

const GESTURE_ZONE_SIZE_PX = 56;
const GESTURE_TAP_COUNT = 7;
const GESTURE_WINDOW_MS = 4000;

type AppVersionInfoModalProps = {
    backendUrl: string;
};

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

/**
 * AppVersionInfoModal
 *
 * Provides an app-wide hidden diagnostics modal for support and QA. It receives
 * the resolved backend URL from App.tsx, depends on React Router location and
 * shared version metadata, and registers global click/keyboard listeners so
 * the modal can be opened without adding public UI to individual pages.
 */
export default function AppVersionInfoModal({ backendUrl }: AppVersionInfoModalProps) {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const tapTimestampsRef = useRef<number[]>([]);
    const openTimerRef = useRef<number | null>(null);

    useEffect(() => {
        const openModal = () => {
            tapTimestampsRef.current = [];
            setIsOpen(true);
        };

        const scheduleModalOpen = () => {
            if (openTimerRef.current !== null) {
                window.clearTimeout(openTimerRef.current);
            }

            openTimerRef.current = window.setTimeout(() => {
                openTimerRef.current = null;
                openModal();
            }, 0);
        };

        const handleClick = (event: MouseEvent) => {
            if (isOpen || event.button !== 0 || event.clientX > GESTURE_ZONE_SIZE_PX || event.clientY > GESTURE_ZONE_SIZE_PX) {
                return;
            }

            const now = Date.now();
            tapTimestampsRef.current = [...tapTimestampsRef.current, now]
                .filter((timestamp) => now - timestamp <= GESTURE_WINDOW_MS);

            if (tapTimestampsRef.current.length >= GESTURE_TAP_COUNT) {
                scheduleModalOpen();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                isOpen
                || isEditableTarget(event.target)
                || !event.ctrlKey
                || !event.altKey
                || !event.shiftKey
                || event.key.toLowerCase() !== 'v'
            ) {
                return;
            }

            openModal();
        };

        window.addEventListener('click', handleClick);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('click', handleClick);
            window.removeEventListener('keydown', handleKeyDown);
            if (openTimerRef.current !== null) {
                window.clearTimeout(openTimerRef.current);
                openTimerRef.current = null;
            }
        };
    }, [isOpen]);

    const route = `${location.pathname}${location.search}${location.hash}`;
    const appVersionText = formatVersion(APP_VERSION);
    const pageVersion = getPageVersionForPath(location.pathname);
    const pageVersionText = formatVersion(pageVersion);
    const frontendOrigin = window.location.origin;

    return (
        <Modal
            show={isOpen}
            onHide={() => setIsOpen(false)}
            centered
            data-testid="app-version-info-modal"
            aria-labelledby="app-version-info-title"
        >
            <Modal.Header closeButton>
                <Modal.Title id="app-version-info-title" as="h2" className="fs-5">
                    Application information
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <dl className="row mb-0 small">
                    <dt className="col-5 text-secondary">App version</dt>
                    <dd className="col-7 fw-semibold" data-testid="app-version-value">{appVersionText}</dd>

                    <dt className="col-5 text-secondary">App version created</dt>
                    <dd className="col-7 text-break" data-testid="app-version-created-at-value">{APP_VERSION.createdAt}</dd>

                    <dt className="col-5 text-secondary">Page version</dt>
                    <dd className="col-7 fw-semibold" data-testid="page-version-value">{pageVersionText}</dd>

                    <dt className="col-5 text-secondary">Page version created</dt>
                    <dd className="col-7 text-break" data-testid="page-version-created-at-value">{pageVersion.createdAt}</dd>

                    <dt className="col-5 text-secondary">Current route</dt>
                    <dd className="col-7 text-break" data-testid="app-route-value">{route}</dd>

                    <dt className="col-5 text-secondary">Build time</dt>
                    <dd className="col-7 text-break" data-testid="app-build-time-value">{BUILD_TIME}</dd>

                    <dt className="col-5 text-secondary">Frontend origin</dt>
                    <dd className="col-7 text-break" data-testid="frontend-origin-value">{frontendOrigin}</dd>

                    <dt className="col-5 text-secondary">Backend API</dt>
                    <dd className="col-7 text-break" data-testid="backend-url-value">{backendUrl}</dd>
                </dl>
            </Modal.Body>
        </Modal>
    );
}
